import React, { useEffect, useRef, useState } from "react";
import { CardCP } from "submodules/nerit-framework-ui/common/components/card/CardCP";
import {
  ChartActionsCP,
  ChartActionsTP,
  ChartRatioSizeTP,
  ChartTypeTP,
} from "modules/reports/components/chart-actions/ChartActionsCP";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { individualFullExportOptions } from "modules/reports/components/charts/chart-export/IndividualFullExport";
import { exportReadabilityOptions } from "modules/reports/components/charts/chart-export/ExportReadability";
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/offline-exporting")(Highcharts);

/* TypeScript 3.7 não inclui ResizeObserver na lib DOM. */
declare class ResizeObserver {
  constructor(callback: () => void);
  observe(target: Element): void;
  disconnect(): void;
}

interface ICPProps {
  alwaysShowLabels?: boolean;
  chartOptions: Highcharts.Options;
  chartType: ChartTypeTP;
  buttonsToHide?: ChartActionsTP[];
  hideTitle?: boolean;
  cardTitle?: string;
  width?: string;
  footer?: JSX.Element;
  height?: number;
  onChartCreated?: (chart: Highcharts.Chart) => void;
  initialRatioSize?: ChartRatioSizeTP;
  resolveChartSize?: (
    hostWidth: number,
  ) => { width: number; height: number } | null;
}

/**
 */
function getChartRatioSize(ratio: ChartRatioSizeTP, fullHeight?: number) {
  if (ratio === "16:9") {
    return { width: 854, height: 480 };
  }
  if (ratio === "4:3") {
    return { width: 640, height: 480 };
  }
  return { width: undefined, height: fullHeight ? fullHeight : 350 };
}

/**
 */
export function CardHighChartCP(props: ICPProps): JSX.Element {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const hostRef = useRef<HTMLDivElement>(null);

  const [chartType, setChartType] = useState<ChartTypeTP>(props.chartType);
  useEffect(onChangeChartType, [chartType]);

  const [linearScale, setLinearScale] = useState<boolean>(true);
  useEffect(onChangeScale, [linearScale]);

  const [ratioSize, setRatioSize] = useState<ChartRatioSizeTP>(
    props.initialRatioSize ?? "full",
  );
  const ratioSizeRef = useRef(ratioSize);
  ratioSizeRef.current = ratioSize;
  useEffect(onChangeRatioSize, [ratioSize, props.resolveChartSize]);
  useEffect(observeHostWidth, [props.resolveChartSize]);

  const [showLabels, setShowLabels] = useState<boolean>(false);
  useEffect(onChangeShowDataLabels, [showLabels]);

  /**
   */
  function onChangeChartType(): void {
    /* Para evitar que o gráfico volte para o tamanho padrão ao mudar o tipo. */
    onChangeRatioSize();

    chartComponentRef.current?.chart.update({
      chart: { type: chartType === "bar" ? "column" : chartType },
    });
  }

  /**
   */
  function onChangeRatioSize(): void {
    const chart = chartComponentRef.current?.chart;
    if (!chart) {
      return;
    }

    if (props.resolveChartSize) {
      const hostWidth = hostRef.current?.clientWidth ?? 0;
      const next = props.resolveChartSize(hostWidth);
      if (!next || next.width <= 0 || next.height <= 0) {
        return;
      }
      if (
        chart.chartWidth === next.width &&
        chart.chartHeight === next.height
      ) {
        return;
      }
      chart.update({
        chart: {
          width: next.width,
          height: next.height,
        },
      });
      return;
    }

    const { width, height } = getChartRatioSize(ratioSize, props.height);
    if (width === undefined) {
      chart.update({
        chart: {
          width: undefined,
          height,
        },
      });
      return;
    }

    chart.update({
      chart: {
        width,
        height,
      },
    });
  }

  /**
   */
  function observeHostWidth(): void | (() => void) {
    const host = hostRef.current;
    if (!host || !props.resolveChartSize) {
      return;
    }

    const observer = new ResizeObserver(() => {
      onChangeRatioSize();
    });
    observer.observe(host);

    return () => observer.disconnect();
  }

  function onChangeScale(): void {
    /* Para evitar que o gráfico volte para o tamanho padrão ao mudar a escala. */
    onChangeRatioSize();

    chartComponentRef.current?.chart.update({
      yAxis: {
        type: linearScale ? "linear" : "logarithmic",
      },
    });
  }

  /**
   */
  function onChangeShowDataLabels(): void {
    /* Para evitar que o gráfico volte para o tamanho padrão ao mostrar os rótulos. */
    onChangeRatioSize();

    chartComponentRef.current?.chart.update({
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: props.alwaysShowLabels
              ? props.alwaysShowLabels
              : showLabels,
            formatter: function () {
              return this.y != null ? this.y.toLocaleString("pt-BR") : "";
            },
          },
        },
        column: {
          dataLabels: {
            enabled: props.alwaysShowLabels
              ? props.alwaysShowLabels
              : showLabels,
            formatter: function () {
              return this.y != null ? this.y.toLocaleString("pt-BR") : "";
            },
          },
        },
        line: {
          dataLabels: {
            enabled: props.alwaysShowLabels
              ? props.alwaysShowLabels
              : showLabels,
            formatter: function () {
              return this.y != null ? this.y.toLocaleString("pt-BR") : "";
            },
          },
        },
        scatter: {
          dataLabels: {
            enabled: props.alwaysShowLabels
              ? props.alwaysShowLabels
              : showLabels,
            formatter: function () {
              return this.y != null ? this.y.toLocaleString("pt-BR") : "";
            },
          },
        },
      },
    });
  }

  function showsRatioSelector(): boolean {
    return (
      !props.hideTitle && !props.buttonsToHide?.includes("fixedRatioSize")
    );
  }

  function exportMenuItems(): Array<string | Highcharts.ExportingMenuObject> {
    if (!showsRatioSelector()) {
      return [
        "viewFullscreen",
        "printChart",
        "separator",
        "downloadPNG",
        "downloadJPEG",
        "downloadPDF",
        "downloadSVG",
      ];
    }

    function exportThisChart(
      this: Highcharts.Chart,
      type: Highcharts.ExportingMimeTypeValue,
    ): void {
      /* exportChartLocal vem do modulo offline-exporting e nao faz parte do tipo Chart. */
      const chart = this as Highcharts.Chart & {
        exportChartLocal(
          options?: Highcharts.ExportingOptions,
          chartOptions?: Highcharts.Options,
        ): void,
      };
      chart.exportChartLocal(
        ratioSizeRef.current === "full"
          ? { type, ...individualFullExportOptions(this.chartWidth) }
          : { type },
        exportReadabilityOptions(this),
      );
    }

    return [
      "viewFullscreen",
      "printChart",
      "separator",
      {
        textKey: "downloadPNG",
        onclick: function (this: Highcharts.Chart) {
          exportThisChart.call(this, "image/png");
        },
      },
      {
        textKey: "downloadJPEG",
        onclick: function (this: Highcharts.Chart) {
          exportThisChart.call(this, "image/jpeg");
        },
      },
      {
        textKey: "downloadPDF",
        onclick: function (this: Highcharts.Chart) {
          exportThisChart.call(this, "application/pdf");
        },
      },
      {
        textKey: "downloadSVG",
        onclick: function (this: Highcharts.Chart) {
          exportThisChart.call(this, "image/svg+xml");
        },
      },
    ];
  }

  return (
    <CardCP
      width={props.width}
      innerSpacing={"none"}
      title={props.cardTitle}
      titleRight={
        props.hideTitle ? undefined : (
          <ChartActionsCP
            linearScale={{ selected: linearScale, onChange: setLinearScale }}
            chartType={{ selected: chartType, onChange: setChartType }}
            fixedRatioSize={{
              selected: ratioSize,
              onChange: setRatioSize,
            }}
            buttonsToHide={props.buttonsToHide}
            showLabels={{ selected: showLabels, onChange: setShowLabels }}
          />
        )
      }
    >
      <div
        ref={hostRef}
        style={{
          height: "auto",
          width: props.resolveChartSize ? "100%" : "auto",
          marginTop: 20,
        }}
      >
        <HighchartsReact
          highcharts={Highcharts}
          options={{
            credits: {
              enabled: false,
            },
            title: {
              text: "",
            },
            ...props.chartOptions,
            exporting: {
              enabled: true,
              ...props.chartOptions.exporting,
              fallbackToExportServer: false,
              buttons: {
                contextButton: {
                  menuItems: exportMenuItems() as Array<string>,
                },
              },
            },
          }}
          callback={props.onChartCreated}
          ref={chartComponentRef}
        />
      </div>

      {props.footer}
    </CardCP>
  );
}
